<template>
  <div class="container pull-up">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">{{ $t('status.loading') }}</span>
      </div>
      <p class="mt-3 text-muted">{{ $t('status.loading') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <h4 class="alert-heading">{{ $t('error.title') }}</h4>
      <p>{{ error }}</p>
      <hr>
      <button class="btn btn-outline-danger" @click="fetchCustomerData">
        <i class="mdi mdi-refresh"></i> {{ $t('button.retry') }}
      </button>
    </div>

    <!-- Main Content -->
    <div v-else class="row">
      <!-- Customer Quick Insights Cards -->
      <div class="col-md-12">
        <div class="row">
          <div class="col-md-3">
            <div class="card m-b-30">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <div class="avatar avatar-md">
                    <div class="avatar-title bg-primary rounded-circle">
                      <i class="mdi mdi-account-check"></i>
                    </div>
                  </div>
                  <div class="ml-3">
                    <h5 class="mb-0">{{ totalVisits }}</h5>
                    <p class="text-muted mb-0">{{ $t('customer.label.total_visits') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card m-b-30">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <div class="avatar avatar-md">
                    <div class="avatar-title bg-success rounded-circle">
                      <i class="mdi mdi-currency-usd"></i>
                    </div>
                  </div>
                  <div class="ml-3">
                    <h5 class="mb-0">{{ formatCurrency(totalSpent) }}</h5>
                    <p class="text-muted mb-0">{{ $t('customer.label.total_spent') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card m-b-30">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <div class="avatar avatar-md">
                    <div class="avatar-title bg-warning rounded-circle">
                      <i class="mdi mdi-star"></i>
                    </div>
                  </div>
                  <div class="ml-3">
                    <h5 class="mb-0">{{ totalPoints }}</h5>
                    <p class="text-muted mb-0">{{ $t('customer.label.total_points') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card m-b-30">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <div class="avatar avatar-md">
                    <div class="avatar-title bg-info rounded-circle">
                      <i class="mdi mdi-calendar-clock"></i>
                    </div>
                  </div>
                  <div class="ml-3">
                    <h5 class="mb-0">{{ lastVisit ? $slimDate(lastVisit) : $t('customer.label.no_visits') }}</h5>
                    <p class="text-muted mb-0">{{ $t('customer.label.last_visit') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Customer Details -->
      <div class="col-md-4">
        <div class="card m-b-30">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="mdi mdi-account-details"></i> {{ $t('customer.title.details') }}
            </h5>
          </div>
          <div class="card-body">
            <div class="text-center mb-4">
              <div class="customer-avatar">
                <img
                  v-if="customer.photo && customer.photo !== ''"
                  :src="customer.photo"
                  alt="Customer Photo"
                  class="rounded-circle img-thumbnail"
                  style="width: 100px; height: 100px; object-fit: cover;"
                  @error="handleImageError">
                <div
                  v-else
                  class="rounded-circle bg-light d-flex align-items-center justify-content-center"
                  style="width: 100px; height: 100px; margin: 0 auto;">
                  <i class="mdi mdi-account text-muted" style="font-size: 2rem;"></i>
                </div>
              </div>
              <h4 class="mt-3">{{ customer.name || $t('customer.label.not_available') }}</h4>
              <p class="text-muted">{{ customer.code || $t('customer.label.not_available') }}</p>
            </div>
            <div class="customer-info">
              <div class="info-item d-flex justify-content-between mb-2">
                <span class="text-muted">{{ $t('customer.label.email') }}:</span>
                <span>{{ customer.email || $t('customer.label.not_available') }}</span>
              </div>
              <div class="info-item d-flex justify-content-between mb-2">
                <span class="text-muted">{{ $t('customer.label.phone') }}:</span>
                <span>{{ formatPhone(customer.phone) || $t('customer.label.not_available') }}</span>
              </div>
              <div class="info-item d-flex justify-content-between mb-2">
                <span class="text-muted">{{ $t('customer.label.address') }}:</span>
                <span>{{ customer.address || $t('customer.label.not_available') }}</span>
              </div>
              <div class="info-item d-flex justify-content-between mb-2">
                <span class="text-muted">{{ $t('customer.label.gender') }}:</span>
                <span>{{ formatGender(customer.gender) }}</span>
              </div>
              <div class="info-item d-flex justify-content-between mb-2">
                <span class="text-muted">{{ $t('customer.label.country') }}:</span>
                <span>{{ customer.nationality ? customer.nationality.name : $t('customer.label.not_available') }}</span>
              </div>
              <div class="info-item d-flex justify-content-between mb-2">
                <span class="text-muted">{{ $t('customer.label.price_list') }}:</span>
                <span>{{ customer.price_list ? customer.price_list.name : $t('customer.label.not_available') }}</span>
              </div>
              <div class="info-item d-flex justify-content-between mb-2">
                <span class="text-muted">{{ $t('customer.label.buy_on_credit') }}:</span>
                <span>
                  <el-switch :value="customer.buy_on_credit" disabled></el-switch>
                </span>
              </div>
              <div class="info-item d-flex justify-content-between mb-2">
                <span class="text-muted">{{ $t('customer.label.created_at') }}:</span>
                <span>{{ $slimDate(customer.created_at) }}</span>
              </div>
            </div>
            <div class="mt-4">
              <el-button
                type="primary"
                size="small"
                @click="editCustomer"
                icon="el-icon-edit"
                :loading="actionLoading.edit">
                {{ $t('button.edit') }}
              </el-button>
              <el-button
                type="danger"
                size="small"
                @click="archiveCustomer"
                icon="el-icon-delete"
                :loading="actionLoading.archive">
                {{ $t('button.archive') }}
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Purchases -->
      <div class="col-md-8">
        <div class="card m-b-30">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="mdi mdi-cart"></i> {{ $t('customer.title.recent_purchases') }}
            </h5>
          </div>
          <div class="card-body">
            <!-- Empty State -->
            <div v-if="!purchases || purchases.length === 0" class="text-center py-5">
              <i class="mdi mdi-cart-outline text-muted" style="font-size: 3rem;"></i>
              <h5 class="mt-3 text-muted">{{ $t('customer.label.no_purchases') }}</h5>
              <p class="text-muted">{{ $t('customer.label.no_purchases_description') }}</p>
            </div>

            <!-- Purchases Table -->
            <div v-else>
              <el-table
                :data="purchases"
                v-loading="purchasesLoading"
                style="width: 100%"
                :default-sort="{ prop: 'created_at', order: 'descending' }">
                <el-table-column prop="id" :label="$t('sale.label.id')" width="80" sortable></el-table-column>
                <el-table-column prop="code" :label="$t('sale.label.code')" width="120" sortable></el-table-column>
                <el-table-column prop="created_at" :formatter="$slimDate" :label="$t('label.date')" width="120" sortable></el-table-column>
                <el-table-column prop="total_price" :label="$t('sale.label.total')" width="120" sortable>
                  <template #default="{ row }">{{ formatPrice(row.total_price) }}</template>
                </el-table-column>
                <el-table-column prop="items_count" :label="$t('sale.label.items_count')" width="100" align="center"></el-table-column>
                <el-table-column :label="$t('sale.label.cashier')" width="150">
                  <template slot-scope="scope">
                    <span v-if="scope.row.cashier">
                      {{ scope.row.cashier.name }}
                    </span>
                    <span v-else>{{ $t('customer.label.not_available') }}</span>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('sale.label.payment_status')" width="120" align="center">
                  <template slot-scope="scope">
                    <el-tag
                      :type="getPaymentStatusType(scope.row)"
                      size="small">
                      {{ getPaymentStatus(scope.row) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('title.actions')" width="120" align="center">
                  <template slot-scope="scope">
                    <el-button type="text" size="small" @click="viewPurchaseDetails(scope.row)">
                      {{ $t('button.view') }}
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <!-- Pagination - Moved outside the v-else block to always be visible -->
            <div class="mt-3 d-flex justify-content-end" v-if="purchasesPagination.total > 0">
              <Pagination
                :total="purchasesPagination.total"
                :current-page="+purchasesPagination.current_page"
                :page-size="+purchasesPagination.per_page"
                @current-change="handlePurchasePageChange"
                @size-change="handlePurchaseSizeChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'
import Pagination from '@/components/_Partials/Pagination.vue'

export default {
  name: 'CustomerDetails',
  components: {
    Pagination
  },
  mixins: [ErrorHandlingMixin],
  data () {
    return {
      customer: {},
      purchases: [],
      totalVisits: 0,
      totalSpent: 0,
      totalPoints: 0,
      lastVisit: null,
      loading: false,
      error: null,
      purchasesLoading: false,
      actionLoading: {
        edit: false,
        archive: false
      },
      purchasesPagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      }
    }
  },
  computed: {
    customerId () {
      return this.$route.params.id
    },
    customerCode () {
      return this.$route.params.code
    }
  },
  methods: {
    formatGender (gender) {
      if (!gender) return this.$t('customer.label.not_available')
      return gender.toLowerCase() === 'm' ? this.$t('customer.label.male') : this.$t('customer.label.female')
    },
    formatPhone (phone) {
      if (!phone) return this.$t('customer.label.not_available')
      // Basic phone formatting - you can enhance this based on your needs
      return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    },
    handleImageError (event) {
      // Hide the broken image and show placeholder
      event.target.style.display = 'none'
      const placeholder = event.target.nextElementSibling
      if (placeholder) {
        placeholder.style.display = 'flex'
      }
    },
    askQuestion (message, callback) {
      this.$confirm(message, this.$t('button.confirm'), {
        confirmButtonText: this.$t('button.confirm'),
        cancelButtonText: this.$t('button.cancel'),
        type: 'warning'
      }).then(callback).catch(() => {
        // User cancelled
      })
    },
    getPaymentStatus (purchase) {
      if (!purchase) return this.$t('customer.label.not_available')

      if (purchase.paid >= purchase.total_price) {
        return this.$t('sale.status.paid')
      } else if (purchase.paid > 0) {
        return this.$t('sale.status.partial')
      } else {
        return this.$t('sale.status.unpaid')
      }
    },
    getPaymentStatusType (purchase) {
      if (!purchase) return 'info'

      if (purchase.paid >= purchase.total_price) {
        return 'success'
      } else if (purchase.paid > 0) {
        return 'warning'
      } else {
        return 'danger'
      }
    },
    handlePurchasePageChange (page) {
      this.purchasesPagination.current_page = page
      this.fetchCustomerData()
    },
    handlePurchaseSizeChange (size) {
      this.purchasesPagination.per_page = size
      this.purchasesPagination.current_page = 1
      this.fetchCustomerData()
    },
    async fetchCustomerData () {
      try {
        this.loading = true
        this.error = null

        const customerId = this.customerId

        if (!customerId) {
          this.error = this.$t('customer.error.invalid_id')
          return
        }

        // Fetch customer details using the new endpoint with pagination
        const response = await this.$customerService.getCustomerDetails(customerId, {
          page: this.purchasesPagination.current_page,
          per_page: this.purchasesPagination.per_page
        })

        if (response.status === 'success') {
          this.customer = response.customer || {}
          this.purchases = response.purchases?.data || []

          // Update pagination info
          if (response.purchases) {
            this.purchasesPagination = {
              current_page: response.purchases.current_page || 1,
              per_page: response.purchases.per_page || 10,
              total: response.purchases.total || 0,
              last_page: response.purchases.last_page || 1
            }
          }

          // Set meta data with proper fallbacks
          if (response.meta) {
            this.totalVisits = response.meta.total_visits || 0
            this.totalSpent = response.meta.total_spent || 0
            this.totalPoints = response.meta.total_points || 0
            this.lastVisit = response.meta.last_visit || null
          }

          // Validate that we have customer data
          if (!this.customer.id) {
            this.error = this.$t('customer.error.not_found')
            return
          }
        } else {
          this.error = response.message || this.$t('customer.error.fetch_failed')
        }
      } catch (error) {
        console.error('Error fetching customer data:', error)
        this.error = this.$t('customer.error.fetch_failed')

        // Handle specific error cases
        if (error.response?.status === 404) {
          this.error = this.$t('customer.error.not_found')
        } else if (error.response?.status >= 500) {
          this.error = this.$t('error.server_error')
        } else if (error.message === 'Network Error') {
          this.error = this.$t('error.network_error')
        }
      } finally {
        this.loading = false
      }
    },
    editCustomer () {
      if (!this.customer.id) {
        this.showWarning(this.$t('customer.error.invalid_customer'))
        return
      }

      this.$router.push({
        name: 'edit-customer',
        params: { id: this.customer.id }
      })
    },
    archiveCustomer () {
      if (!this.customer.id) {
        this.showWarning(this.$t('customer.error.invalid_customer'))
        return
      }

      this.askQuestion(this.$t('customer.confirm.archive'), async () => {
        try {
          this.actionLoading.archive = true
          await this.$customerService.archiveCustomer(this.customer.id)
          this.showSuccess(this.$t('customer.message.archived'))
          this.$router.push({ name: 'customers' })
        } catch (error) {
          console.error('Error archiving customer:', error)
          this.showWarning(this.$t('customer.error.archive_failed'))
        } finally {
          this.actionLoading.archive = false
        }
      })
    },
    viewPurchaseDetails (purchase) {
      if (!purchase || !purchase.id) {
        this.showWarning(this.$t('customer.error.invalid_purchase'))
        return
      }

      // Navigate to purchase details if route exists, otherwise show modal or details
      if (this.$router.resolve({ name: 'purchase-details', params: { id: purchase.id } }).route.name) {
        this.$router.push({
          name: 'purchase-details',
          params: { id: purchase.id }
        })
      } else {
        // Fallback: show purchase details in a modal or alert
        this.showInfo(`${this.$t('sale.label.id')}: ${purchase.id} - ${this.$t('sale.label.total')}: ${this.formatCurrency(purchase.total_price)}`)
      }
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('customer.title.customer_details'))
    await this.fetchCustomerData()
  },
  watch: {
    // Watch for route changes to refetch data
    '$route.params.id': {
      handler: function (newId) {
        if (newId && newId !== this.customerId) {
          this.fetchCustomerData()
        }
      },
      immediate: false
    }
  }
}
</script>

<style scoped>
.customer-info {
  border-top: 1px solid #eee;
  padding-top: 15px;
}
.info-item {
  padding: 5px 0;
}
</style>
